/**
 * Prompt-library domain types (TO-327) — the versioned, tenant-scoped source of
 * truth for every LLM prompt on the platform (ADR-0039 rule 4).
 *
 * A prompt has a mutable **head** ({@link IPrompt}) and immutable published
 * **version snapshots** ({@link IPromptVersion}). Publishing freezes everything a
 * run needs to be reproducible; nothing on a version can change afterwards.
 *
 * All string vocabularies are TypeScript enums persisted as `varchar` (never a
 * Postgres ENUM) per CLAUDE.md "Enums Over Literals".
 */

/**
 * A prompt's audience — NOT its task. Drives per-type tenant default config and
 * default merging; it never chooses a provider or model (the ai-router registry
 * owns execution). See ADR "Prompt type is audience, not task" (reconciled with
 * ADR-0039: this stays an audience/default axis only).
 */
export enum PromptType {
	/** Prompts shown to / driven by end users (marketing copy, UI text). */
	USER = 'user',
	/** System prompts steering an agent or chain. */
	SYSTEM = 'system',
	/** Validation / judging prompts (deterministic, low-temperature intent). */
	VALIDATION = 'validation',
}

/** The head's lifecycle. A prompt is created `draft`, published, then archived. */
export enum PromptLifecycle {
	DRAFT = 'draft',
	PUBLISHED = 'published',
	ARCHIVED = 'archived',
}

/**
 * A declared variable's type — governs how {@link renderPrompt} substitutes it.
 * `list`/`enum` values are validated against their options; `each` renders an
 * item template per element; `join` concatenates other resolved keys.
 */
export enum PromptVariableType {
	TEXT = 'text',
	ENUM = 'enum',
	LIST = 'list',
	NUMBER = 'number',
	BOOLEAN = 'boolean',
	DATE = 'date',
	EACH = 'each',
	JOIN = 'join',
}

/**
 * The movable environment pointers a prompt can carry. `LATEST` always follows
 * the newest publish; `PRODUCTION` follows only while unpinned (repointing it
 * pins it, so a rollback holds).
 */
export enum PromptLabelName {
	LATEST = 'latest',
	PRODUCTION = 'production',
}

/** Terminal and in-flight states of an evaluation run. */
export enum EvalRunStatus {
	PENDING = 'pending',
	RUNNING = 'running',
	COMPLETED = 'completed',
	FAILED = 'failed',
}

/**
 * One case's outcome. `ERRORED` (the case never produced a judgeable output) is
 * distinct from `FAILED` (it produced one that failed an assertion) — an errored
 * case counts as NOT passing: "we could not tell" is never a pass.
 */
export enum EvalCaseOutcome {
	PASSED = 'passed',
	FAILED = 'failed',
	ERRORED = 'errored',
}

/**
 * The assertion kinds an evaluation case can carry. All but `LLM_RUBRIC` are
 * deterministic and run in-process; `LLM_RUBRIC` is judged by the EU-locked
 * ai-router via {@link AiUseCase.PROMPT_EVAL_JUDGE}.
 */
export enum PromptAssertionType {
	CONTAINS = 'contains',
	NOT_CONTAINS = 'not_contains',
	MAX_LENGTH = 'max_length',
	MATCHES_OUTPUT_SCHEMA = 'matches_output_schema',
	LLM_RUBRIC = 'llm_rubric',
}

/** How a coverage/label lookup treats an absent match (reserved for TO-322 coverage). */
export enum MatchMode {
	STRICT = 'strict',
	LENIENT = 'lenient',
}

/** A declared `{{variable}}` on a prompt head/version. */
export interface PromptVariable {
	key: string
	type: PromptVariableType
	/** Human label for the authoring UI. */
	label?: string
	/** Default used when a render omits the key (before erroring). */
	defaultValue?: unknown
	/** For `enum`: the allowed literal options. */
	options?: string[]
	/** For `list`: the reusable list's code, resolved to items (frozen on publish). */
	listCode?: string
	/** For `each`: the per-item template rendered over the list. */
	itemTemplate?: string
	/** For `join`: the keys whose resolved values are concatenated. */
	joinKeys?: string[]
	/** For `join`: the separator between joined values. */
	joinSeparator?: string
}

/** One reusable option in a {@link IPromptVariableList}. */
export interface PromptVariableListItem {
	value: string
	label: string
}

/**
 * Frozen, reporting-only model hints. The ai-router registry stays authoritative
 * for the provider/model/token-budget actually used at execution; these hints are
 * captured on a version for reproducibility and display, never to select a model.
 */
export interface PromptModelConfig {
	model?: string
	temperature?: number
	maxOutputTokens?: number
	effort?: string
}

/** A single assertion evaluated against a case's output. */
export interface PromptAssertion {
	type: PromptAssertionType
	/** For CONTAINS/NOT_CONTAINS: the substring. For LLM_RUBRIC: the criterion. */
	value?: string
	/** For MAX_LENGTH: the character ceiling. */
	max?: number
}

/** The mutable prompt head. */
export interface IPrompt {
	promptId: string
	tenantId: string
	/** Unique per tenant across ALL rows incl. soft-deleted; immutable; public handle. */
	code: string
	name: string
	description: string | null
	type: PromptType
	body: string
	variables: PromptVariable[]
	lifecycle: PromptLifecycle
	/** 0 = never published; otherwise the latest frozen version number. */
	currentVersion: number
	/** Head overrides merged over the tenant default at publish; null = inherit. */
	modelConfig: PromptModelConfig | null
	/** JSON Schema for structured output; null = plain text. */
	outputSchema: Record<string, unknown> | null
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
	/** Derived from `deletedAt` (never stored); a prompt is deleted iff set. */
	isDeleted: boolean
}

/** An immutable published version snapshot — reproducible in isolation. */
export interface IPromptVersion {
	promptVersionId: string
	promptId: string
	tenantId: string
	version: number
	body: string
	variables: PromptVariable[]
	/** Items of every referenced list, frozen at publish, keyed by list code. */
	listSnapshot: Record<string, PromptVariableListItem[]>
	/** Fully-merged effective hints (never null on a version). */
	modelConfig: PromptModelConfig
	outputSchema: Record<string, unknown> | null
	/** Active cases frozen at publish; null on pre-column versions, [] = none active. */
	testCaseSnapshot: PromptTestCaseSnapshot[] | null
	publishedBy: string
	publishedAt: Date
}

/** A test case frozen into a version (id-stripped; a rerun evaluates the head's cases). */
export interface PromptTestCaseSnapshot {
	name: string
	values: Record<string, unknown>
	assertions: PromptAssertion[]
}

/** A reusable option list, referenced by `list` variables and frozen per version. */
export interface IPromptVariableList {
	promptVariableListId: string
	tenantId: string
	/** Unique per tenant across all rows incl. soft-deleted. */
	code: string
	name: string
	description: string | null
	items: PromptVariableListItem[]
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
	/** Derived from `deletedAt` (never stored). */
	isDeleted: boolean
}

/** Per-tenant, per-type default model config merged into a publish freeze. */
export interface IPromptDefaultConfig {
	promptDefaultConfigId: string
	tenantId: string
	promptType: PromptType
	modelConfig: PromptModelConfig
	createdAt: Date
	updatedAt: Date
}

/** Tenant-wide library settings (one row per tenant). */
export interface IPromptTenantSettings {
	promptTenantSettingsId: string
	tenantId: string
	/** When true, publishing a not-passing prompt is refused (the eval gate blocks). */
	requirePassingEvals: boolean
	createdAt: Date
	updatedAt: Date
}

/** A movable environment pointer at one version of one prompt. */
export interface IPromptLabel {
	promptLabelId: string
	tenantId: string
	promptId: string
	label: PromptLabelName
	promptVersionId: string
	/** A pinned label does not move on publish (rollback holds). */
	pinned: boolean
	updatedBy: string
	updatedAt: Date
}

/** How a test case was authored. Editing a generated case flips it to manual. */
export enum PromptTestCaseOrigin {
	MANUAL = 'manual',
	GENERATED = 'generated',
}

/** An evaluation test case living on the head. */
export interface IPromptTestCase {
	promptTestCaseId: string
	tenantId: string
	promptId: string
	name: string
	values: Record<string, unknown>
	assertions: PromptAssertion[]
	origin: PromptTestCaseOrigin
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
	/** Derived from `deletedAt` (never stored). */
	isDeleted: boolean
}

/** An evaluation run over a target (a version, or the head). */
export interface IPromptEvalRun {
	promptEvalRunId: string
	tenantId: string
	promptId: string
	/** The evaluated version; null = a head run. */
	promptVersionId: string | null
	status: EvalRunStatus
	totalCases: number
	completedCases: number
	passedCases: number
	failedCases: number
	erroredCases: number
	/** Run-level failure (dispatch died), distinct from an assertion failure. */
	failureReason: string | null
	triggeredBy: string | null
	createdAt: Date
	updatedAt: Date
	settledAt: Date | null
}

/** One case's result within a run. Idempotency key: (evalRunId, testCaseId). */
export interface IPromptEvalCaseResult {
	promptEvalCaseResultId: string
	tenantId: string
	promptEvalRunId: string
	/** No FK — the result survives the case's later deletion. */
	testCaseId: string
	/** Denormalised so a result reads standalone after the case is gone. */
	caseName: string
	outcome: EvalCaseOutcome
	outputText: string | null
	assertionResults: PromptAssertionResult[]
	/** Execution failure (not an assertion failure). */
	errorReason: string | null
	createdAt: Date
	updatedAt: Date
}

/** Per-assertion pass/fail with the reason. */
export interface PromptAssertionResult {
	type: PromptAssertionType
	passed: boolean
	reason: string | null
}

/**
 * A reference to a versioned prompt, resolved from ai-service. Carried by
 * an ai-router use case instead of an inline prompt string (ADR-0039 rule 4).
 * `label` defaults to `production` when omitted.
 */
export interface PromptRef {
	code: string
	label?: PromptLabelName
}
